/* eslint-disable @typescript-eslint/no-explicit-any */
import type { User } from '../store/authStore';
import { resolveMerchantDashboardKey } from './merchantDashboardKey';

export function minimalUser(email: string): User {
  const local = email.split('@')[0] || 'User';
  return {
    id: 0,
    fullName: local,
    email,
    adminType: '',
    phone: '',
    role: '',
    status: '',
    company: '',
    department: '',
  };
}

/**
 * Unicode-safe base64 for the impersonation hash payload (merchant names/emails aren't guaranteed
 * to be ASCII, and plain btoa() throws on non-Latin1 input).
 */
export function encodeImpersonationPayload(payload: unknown): string {
  const json = JSON.stringify(payload);
  const utf8Escaped = encodeURIComponent(json).replace(
    /%([0-9A-F]{2})/g,
    (_, hex) => String.fromCharCode(parseInt(hex, 16))
  );
  return encodeURIComponent(btoa(utf8Escaped));
}

export function decodeImpersonationPayload(encoded: string): any {
  const utf8Escaped = atob(decodeURIComponent(encoded));
  const json = decodeURIComponent(
    Array.prototype.map
      .call(utf8Escaped, (c: string) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
      .join('')
  );
  return JSON.parse(json);
}

/**
 * Referral-admin impersonation counterpart to normalizeMerchantUser below — MerchantAuthController
 * #impersonateUser returns an ImpersonationSessionResponse shaped differently depending on which
 * kind of account was impersonated (adminId set vs merchantId/merchantSubUserId set), see
 * ImpersonateBridge. AdminServiceImpl#impersonateAdminUser only ever impersonates AdminType.ADMIN
 * accounts (a fellow SUPER_ADMIN can't be impersonated this way), so adminType is safely hardcoded
 * here rather than read off the response — there's no other case this function is used for.
 */
export function normalizeAdminUser(raw: any, fallbackEmail: string): User {
  if (!raw || typeof raw !== 'object') return { ...minimalUser(fallbackEmail), userType: 'ADMIN', adminType: 'ADMIN', role: 'ADMIN' };

  return {
    id: Number(raw.adminId ?? 0) || 0,
    fullName: String(raw.fullName ?? fallbackEmail.split('@')[0] ?? 'User').trim(),
    email: fallbackEmail,
    adminType: 'ADMIN',
    phone: '',
    role: 'ADMIN',
    status: '',
    company: '',
    department: '',
    userType: 'ADMIN',
    moduleAccesses: Array.isArray(raw.moduleAccesses) ? raw.moduleAccesses : [],
  };
}

export function normalizeMerchantUser(raw: any, fallbackEmail: string): User {
  if (!raw || typeof raw !== 'object') return { ...minimalUser(fallbackEmail), userType: 'MERCHANT' };

  return {
    id: Number(raw.merchantSubUserId ?? raw.merchantId ?? 0) || 0,
    fullName: String(raw.fullName ?? fallbackEmail.split('@')[0] ?? 'User').trim(),
    email: fallbackEmail,
    adminType: String(raw.roleCode ?? raw.roleName ?? 'MERCHANT').trim(),
    phone: String(raw.contactPhone ?? raw.phone ?? ''),
    role: String(raw.roleCode ?? 'MERCHANT').trim(),
    status: '',
    company: '',
    department: '',
    userType: 'MERCHANT',
    merchantId: Number(raw.merchantId ?? 0) || undefined,
    merchantSubUserId: Number(raw.merchantSubUserId ?? 0) || undefined,
    moduleAccesses: Array.isArray(raw.moduleAccesses) ? raw.moduleAccesses : [],
    dashboardKey: resolveMerchantDashboardKey(raw),
  };
}
